/**
 * Pure logic helpers for the Plant-SLoc tab (BCM-234 A1 — master data only).
 *
 * These functions have NO React/DOM dependency so they can be imported directly
 * by the component AND by the fast-check property tests (Properties 7, 8, 9).
 *
 * - deriveTable2Options(table1Ids) -> Property 8: option set equals Table 1 ID set
 * - tabBadgeCount(plantRows)       -> Property 9: badge equals plant row count
 * - cascadeRemoveSloc(...)         -> Property 7: client-side cascade removal
 */

/**
 * Derive the selectable options for the Table 2 SLOC multi-select.
 *
 * The option set is EXACTLY the set of Storage Location IDs present in Table 1:
 * no option absent from Table 1 is offered, and every Table 1 ID is offered.
 * Null/undefined entries are ignored; duplicates are collapsed while preserving
 * first-seen order.
 *
 * @param {Array<string|number>} table1Ids - Storage Location IDs from Table 1.
 * @returns {Array<string|number>} The distinct option set.
 */
export const deriveTable2Options = (table1Ids) => {
  if (!Array.isArray(table1Ids)) return [];
  const seen = new Set();
  const options = [];
  for (const id of table1Ids) {
    if (id === undefined || id === null) continue;
    if (!seen.has(id)) {
      seen.add(id);
      options.push(id);
    }
  }
  return options;
};

/**
 * The tab count badge N equals the number of plant rows in Table 2.
 *
 * @param {Array<object>} plantRows - Table 2 plant rows.
 * @returns {number} The plant row count.
 */
export const tabBadgeCount = (plantRows) =>
  Array.isArray(plantRows) ? plantRows.length : 0;

/**
 * Client-side cascade: when a Storage Location ID is deleted from Table 1,
 * remove it from every Table 2 row's associated SLOCs, preserving all other
 * associations and every other row field unchanged.
 *
 * @param {Array<object>} plantRows - Table 2 plant rows; each may carry a
 *   `storageLocationIDs` array of associated SLOC IDs.
 * @param {string|number} deletedSlocId - The SLOC ID removed from Table 1.
 * @returns {Array<object>} A new array of rows with the deleted SLOC removed.
 */
export const cascadeRemoveSloc = (plantRows, deletedSlocId) => {
  if (!Array.isArray(plantRows)) return [];
  return plantRows.map((row) => {
    if (!row || !Array.isArray(row.storageLocationIDs)) return row;
    return {
      ...row,
      storageLocationIDs: row.storageLocationIDs.filter(
        (id) => id !== deletedSlocId
      ),
    };
  });
};

/**
 * Endpoint registry for the Plant-SLoc tab (BCM-234 A1 — master data only).
 *
 * This is the single source of truth the component uses to route every
 * load/save action, enforcing strict read/write separation (Req 12.1–12.5):
 * Table 1 loads via `fetchStorageLocations`, adds via `addStorageLocations`,
 * and soft-deletes via `updateStorageLocations`; Table 2 loads via
 * `fetchPlantStorageLocationMappings`, adds via `addPlantStorageLocationMapping`,
 * and updates/removes via `updatePlantStorageLocationMapping`.
 *
 * Endpoints are intentionally bare path segments (no leading slash); the
 * component composes the full URL with its `buildUrl` helper.
 */
export const SLOC_ENDPOINTS = Object.freeze({
  table1: Object.freeze({
    load: "fetchStorageLocations",
    add: "addStorageLocations",
    softDelete: "updateStorageLocations",
  }),
  table2: Object.freeze({
    load: "fetchPlantStorageLocationMappings",
    add: "addPlantStorageLocationMapping",
    update: "updatePlantStorageLocationMapping",
  }),
});

/** Read_Endpoints: reads MUST use only these (Req 12.1, 12.3, 12.5). */
export const READ_ENDPOINTS = Object.freeze([
  SLOC_ENDPOINTS.table1.load,
  SLOC_ENDPOINTS.table2.load,
]);

/** Write_Endpoints: writes MUST use only these (Req 12.2, 12.4, 12.5). */
export const WRITE_ENDPOINTS = Object.freeze([
  SLOC_ENDPOINTS.table1.add,
  SLOC_ENDPOINTS.table1.softDelete,
  SLOC_ENDPOINTS.table2.add,
  SLOC_ENDPOINTS.table2.update,
]);

/**
 * Classify an endpoint as a read, a write, or unknown.
 * Used to assert strict read/write separation (Req 12.5).
 *
 * @param {string} endpoint - A bare endpoint path segment.
 * @returns {"read"|"write"|"unknown"}
 */
export const classifyEndpoint = (endpoint) => {
  if (READ_ENDPOINTS.includes(endpoint)) return "read";
  if (WRITE_ENDPOINTS.includes(endpoint)) return "write";
  return "unknown";
};

/**
 * Whitespace-only / empty Storage Location IDs are rejected client-side
 * (Req 11.1): such a value must NOT trigger an add call.
 *
 * @param {*} value - The candidate SLOC ID (typically a free-text string).
 * @returns {boolean} true when the value is empty or whitespace-only.
 */
export const isBlankSloc = (value) => String(value ?? "").trim() === "";

/**
 * Diff a plant row's current SLOC selection against its persisted snapshot.
 * Additions route to the Table 2 add Write_Endpoint and removals to the
 * Table 2 update (soft-delete) Write_Endpoint (Req 12.4).
 *
 * @param {Array<string|number>} current - Currently selected SLOC IDs.
 * @param {Array<string|number>} original - Persisted SLOC IDs.
 * @returns {{ added: Array, removed: Array }}
 */
export const diffPlantSlocs = (current, original) => {
  const cur = Array.isArray(current) ? current : [];
  const orig = Array.isArray(original) ? original : [];
  const added = cur.filter((id) => !orig.includes(id));
  const removed = orig.filter((id) => !cur.includes(id));
  return { added, removed };
};

/**
 * Decide how to delete a Table 1 Storage Location row.
 *
 * An UNSAVED (isNew) row is removed locally with NO write/axios call; a
 * PERSISTED row issues a soft-delete write to the Table 1 write endpoint
 * (`updateStorageLocations`) with `markForDelete = true` (Req 12.2).
 *
 * @param {object} row - A Table 1 row; `isNew` marks an unsaved row.
 * @returns {{ localRemove: boolean, write: null | { endpoint: string, record: object } }}
 */
export const planSlocDelete = (row) => {
  if (row && row.isNew) {
    return { localRemove: true, write: null };
  }
  return {
    localRemove: false,
    write: {
      endpoint: SLOC_ENDPOINTS.table1.softDelete,
      record: {
        storageLocationMasterID: row ? row.id : undefined,
        storageLocationID: row ? row.storageLocationID : undefined,
        markForDelete: true,
      },
    },
  };
};
